import type { childPartV2, breakdownProcessV1 } from "@/api/breakdownService";
import excel from "exceljs";
import {
  fetchMaterialCodesBySupplier,
  fetchMaterialDetailByPartId,
  fetchBreakdownProcessV1,
  fetchMaterial2ndTierProcesses,
  fetchMaterialOtherProcesses,
  fetchChildPartV1ByPartId,
} from "./cachedQueries";
import {
  MERGE_CHILDPARTS_COLS,
  MERGE_PARTPROCESS_COLS,
  MERGE_PARTOTHERPROCESS_COLS,
} from "./constants";
import type { MergeRequest, RowData } from "./types";
import type { part } from "@/api/supplierService";
import type { exchangeRatePart } from "@/api/breakdownExchangeRateService";
import type { childPartV1 } from "../../api/breakdownService";
import type { MaterialCodeDatail } from "@/api/materialSupplierService";
import { parseDecimal } from "../inputParse";

export const buildPartRows = async (part: part[]): Promise<RowData[]> => {
  const rowsToInsert: RowData[] = [];
  for (const p of part) {
    const d: RowData = [];
    d[1] = p.part_number;
    d[2] = p.part_name;
    d[3] = p.ac;
    d[4] = p.rt;
    d[5] = p.oem_price.current_price;
    d[6] = p.oem_price.new_price;
    d[7] = p.oem_price.new_price - p.oem_price.current_price;
    d[8] = p.spare_price.current_price;
    d[9] = p.spare_price.new_price;
    d[10] = p.spare_price.new_price - p.spare_price.current_price;
    rowsToInsert.push(d);
  }
  return rowsToInsert;
};

export const buildExchangeRatePartRows = async (
  part: exchangeRatePart[],
): Promise<RowData[]> => {
  const rowsToInsert: RowData[] = [];
  for (const p of part) {
    const d: RowData = [];
    d[1] = p.part_number;
    d[2] = p.part_name;
    d[3] = p.ac;
    d[4] = p.rt;
    d[5] = p.oem_price;
    d[6] = p.spare_price;
    d[7] = p.currency_code;
    d[8] = p.price;
    d[9] = p.exchange_rate;
    d[10] = p.price * p.exchange_rate;
    d[11] = p.total_oem_other_process_cost;
    d[12] = p.total_spare_other_process_cost;
    rowsToInsert.push(d);
  }
  return rowsToInsert;
};

const buildChildPartV1RowData = (
  cp: childPartV1,
  materialCodeDetail: MaterialCodeDatail | undefined,
  breakdownProcess: breakdownProcessV1,
): { d: RowData; colNumber: number } => {
  const d: RowData = [];
  d[1] = cp.child_part_no;
  d[2] = cp.child_part_name;
  d[3] = parseDecimal(String(cp.oem_price), 2);
  d[4] = parseDecimal(String(cp.spare_price), 2);
  d[5] = cp.child_part_info.qty;
  d[6] = materialCodeDetail?.material_type_name || "";
  d[7] = materialCodeDetail?.spec_name || "";
  d[8] = materialCodeDetail?.dimension_name || "";
  d[9] = materialCodeDetail?.supplier_name || "";
  d[10] = materialCodeDetail?.return_scrap_supplier_name || "";
  d[11] = materialCodeDetail?.thickness?.toString() || "";
  d[12] = materialCodeDetail?.width?.toString() || "";
  d[13] = materialCodeDetail?.diameter?.toString() || "";
  d[14] = materialCodeDetail?.shaft_od?.toString() || "";
  d[15] = materialCodeDetail?.round_od?.toString() || "";
  d[16] = materialCodeDetail?.rectangle_x?.toString() || "";
  d[17] = materialCodeDetail?.rectangle_y?.toString() || "";
  d[18] = materialCodeDetail?.length_mm?.toString() || "";
  d[19] = cp.child_part_info.material_code;
  d[20] = parseDecimal(String(cp.child_part_info.material_price ?? 0), 2);
  d[21] = parseDecimal(String(cp.child_part_info.amount ?? 0), 2);
  d[22] = parseDecimal(String(cp.child_part_info.ratio ?? 0), 2);
  d[23] = parseDecimal(String(cp.child_part_info.consumption ?? 0), 3);
  d[24] = parseDecimal(String(cp.child_part_info.part_weight ?? 0), 3);
  d[25] = parseDecimal(String(cp.child_part_info.scrap_weight ?? 0), 3);
  d[26] = parseDecimal(String(cp.total_material_cost ?? 0), 2);
  d[27] = parseDecimal(
    String(cp.child_part_material_cost.material_cost ?? 0),
    2,
  );
  d[28] = cp.child_part_material_cost.is_purchased ? "Yes" : "No";
  d[29] = parseDecimal(
    String(cp.child_part_material_cost.scrap_price ?? 0),
    2,
  );
  d[30] = parseDecimal(String(cp.child_part_material_cost.admin ?? 0), 2);
  d[31] = parseDecimal(String(cp.child_part_material_cost.profit ?? 0), 2);
  let col = 31;
  d[++col] = parseDecimal(
    String(cp.child_part_material_cost.second_tier_process_total ?? 0),
    2,
  );
  if (breakdownProcess?.second_tier_process?.length > 0) {
    const sorted = [...breakdownProcess.second_tier_process].sort(
      (a, b) => a.id - b.id,
    );
    for (const process of sorted) {
      const processCost =
        cp.child_part_material_cost.second_tier_process?.find(
          (p) => p.ms_second_tier_process_v1_id === process.id,
        )?.cost;
      d[++col] = parseDecimal(String(processCost ?? 0), 2);
    }
  }
  d[++col] = cp.child_part_material_cost.second_tier_process_comment;
  d[++col] = parseDecimal(
    String(cp.child_part_material_cost.other_process_total ?? 0),
    2,
  );
  if (breakdownProcess?.other_process?.length > 0) {
    const sorted = [...breakdownProcess.other_process].sort(
      (a, b) => a.id - b.id,
    );
    for (const process of sorted) {
      const processCost = cp.child_part_material_cost.other_process?.find(
        (p) => p.ms_other_process_v1_id === process.id,
      )?.cost;
      d[++col] = parseDecimal(String(processCost ?? 0), 2);
    }
  }
  d[++col] = cp.child_part_material_cost.other_process_comment;
  d[++col] = parseDecimal(
    String(cp.child_part_material_cost.pct ?? 0),
    2,
  );
  d[++col] = parseDecimal(
    String(cp.child_part_material_cost.other_pct ?? 0),
    2,
  );
  d[++col] = parseDecimal(
    String(cp.child_part_process.oem_process_total ?? 0),
    2,
  );
  if (breakdownProcess?.oem_process?.length > 0) {
    const sorted = [...breakdownProcess.oem_process].sort(
      (a, b) => a.id - b.id,
    );
    for (let i = 0; i < sorted.length; i++) {
      d[++col] = parseDecimal(
        String(cp.child_part_process.oem_process[i]?.cost ?? 0),
        2,
      );
    }
  }
  d[++col] = cp.child_part_process.oem_process_comment;
  d[++col] = parseDecimal(
    String(cp.child_part_process.oem_process_admin ?? 0),
    2,
  );
  d[++col] = parseDecimal(
    String(cp.child_part_process.oem_process_profit ?? 0),
    2,
  );
  d[++col] = parseDecimal(
    String(cp.child_part_process.spare_process_total ?? 0),
    2,
  );
  if (breakdownProcess?.spare_process?.length > 0) {
    const sorted = [...breakdownProcess.spare_process].sort(
      (a, b) => a.id - b.id,
    );
    for (let i = 0; i < sorted.length; i++) {
      d[++col] = parseDecimal(
        String(cp.child_part_process.spare_process[i]?.cost ?? 0),
        2,
      );
    }
  }
  d[++col] = cp.child_part_process.spare_process_comment;
  d[++col] = parseDecimal(
    String(cp.child_part_process.spare_process_admin ?? 0),
    2,
  );
  d[++col] = parseDecimal(
    String(cp.child_part_process.spare_process_profit ?? 0),
    2,
  );
  d[++col] = parseDecimal(String(cp.child_part_process.oem_price ?? 0), 2);
  d[++col] = parseDecimal(String(cp.child_part_process.oem_admin ?? 0), 2);
  d[++col] = parseDecimal(String(cp.child_part_process.oem_profit ?? 0), 2);
  d[++col] = parseDecimal(String(cp.child_part_process.spare_price ?? 0), 2);
  d[++col] = parseDecimal(String(cp.child_part_process.spare_admin ?? 0), 2);
  d[++col] = parseDecimal(String(cp.child_part_process.spare_profit ?? 0), 2);
  return { d, colNumber: col };
};

export interface BuildChildPartV1RowsInput {
  childPartsV1: childPartV1[];
  supplierCode: string;
  partId: number;
  sheet: excel.Worksheet;
  insertRow: number;
}

export const buildChildPartV1Rows = async ({
  childPartsV1,
  supplierCode,
  partId,
  sheet,
  insertRow,
}: BuildChildPartV1RowsInput): Promise<{
  rowsToInsert: RowData[];
  colNumber: number;
}> => {
  const rowsToInsert: RowData[] = [];
  let colNumber = 0;

  const [materialCodes, detailsList, breakdownProcess] = await Promise.all([
    fetchMaterialCodesBySupplier(supplierCode || ""),
    fetchMaterialDetailByPartId(partId || 0, "v1"),
    fetchBreakdownProcessV1(supplierCode || ""),
  ]);

  let hCol = 32;

  const insertDynamicColumn = (label: string, bgColor: string) => {
    hCol++; // ขยับ Index ไปยังตำแหน่งที่จะแทรก เพื่อดันข้อมูลเทมเพลตเดิมไปทางขวา
    sheet.spliceColumns(hCol, 0, []);
    sheet.getColumn(hCol).width = Math.max(label.length + 2, 12);
    const targetCell = sheet.getRow(insertRow - 1).getCell(hCol);
    targetCell.value = label;
    targetCell.font = { name: "Calibri", bold: true };
    targetCell.alignment = { vertical: "middle", horizontal: "center" };
    targetCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: `FF${bgColor.replace("#", "")}` },
    };
    targetCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  };

  const insertProcessColumns = (
    processes: Array<{ id: number; name: string }>,
    labelPrefix: string,
    color: string,
  ) => {
    if (!processes?.length) return;
    const sorted = [...processes].sort((a, b) => a.id - b.id);
    for (const process of sorted) {
      insertDynamicColumn(`${labelPrefix}${process.name}`, color);
    }
  };

  insertProcessColumns(
    breakdownProcess?.second_tier_process,
    "2nd Tier Process- ",
    "#c6e0b4",
  );
  hCol += 2; // skip second_tier_process_comment, other_process_total
  insertProcessColumns(
    breakdownProcess?.other_process,
    "Other Process Total- ",
    "#c6e0b4",
  );
  hCol += 4; // skip other_process_comment, percent, other_percent, oem_process_total
  insertProcessColumns(
    breakdownProcess?.oem_process,
    "OEM Process- ",
    "#ffe699",
  );
  hCol += 4; // skip oem_process_admin, oem_process_profit, spare_process_total
  insertProcessColumns(
    breakdownProcess?.spare_process,
    "S/P Process- ",
    "#ffe699",
  );
  sheet.getRow(insertRow - 1).commit();

  const materialCodeDetailsMap = Object.fromEntries(
    detailsList.map((d) => [d.material_id, d]),
  );

  for (const cp of childPartsV1) {
    const materialCodeInfo = materialCodes?.find(
      (mc) => mc.material_code === cp.child_part_info.material_code,
    );
    const materialCodeDetail = materialCodeInfo?.material_id
      ? materialCodeDetailsMap[materialCodeInfo.material_id]
      : undefined;

    const { d, colNumber: rowColNumber } = buildChildPartV1RowData(
      cp,
      materialCodeDetail,
      breakdownProcess,
    );
    colNumber = rowColNumber;
    rowsToInsert.push(d);
  }

  return { rowsToInsert, colNumber };
};

export interface BuildAllPartV1RowsInput {
  parts: part[];
  supplierCode: string;
  sheet: excel.Worksheet;
  insertRow: number;
  onProgress?: (current: number, total: number) => void;
}

// Build rows for the "All Part" V1 export. Compared with the single-part
// layout, four leading columns (Part No./Part Name/A/C/R/T) and two columns
// (Admin/Profit THB/pcs) are inserted, shifting every original column by +6.
export const buildAllPartV1Rows = async ({
  parts,
  supplierCode,
  sheet,
  insertRow,
  onProgress,
}: BuildAllPartV1RowsInput): Promise<{
  rowsToInsert: RowData[];
  colNumber: number;
  separatorOffsets: number[];
}> => {
  const COLUMN_OFFSET = 6;
  const headerRow = insertRow - 1;
  const rowsToInsert: RowData[] = [];
  const separatorOffsets: number[] = [];
  let colNumber = 0;

  const [materialCodes, breakdownProcess] = await Promise.all([
    fetchMaterialCodesBySupplier(supplierCode || ""),
    fetchBreakdownProcessV1(supplierCode || ""),
  ]);

  // ── Insert the new leading / admin-profit header columns ─────────────
  sheet.spliceColumns(1, 0, [], [], [], []); // Part No., Part Name, A/C, R/T
  sheet.spliceColumns(7, 0, [], []); // Admin (THB/pcs), Profit (THB/pcs) after Child Part Name

  // spliceColumns shifts the whole sheet, so the single-column header block
  // (Supplier ID:/Admin:/CBD: ... in column 1) moved to column 5. Move those
  // labels back to column 1 for every row above the table header.
  for (let r = 1; r < headerRow; r++) {
    const src = sheet.getRow(r).getCell(5);
    if (src.value !== null && src.value !== undefined && src.value !== "") {
      const dst = sheet.getRow(r).getCell(1);
      dst.value = src.value;
      dst.font = src.font;
      src.value = null;
    }
  }

  const applyHeaderStyle = (cell: excel.Cell, label: string) => {
    cell.value = label;
    cell.font = { name: "Calibri", bold: true, size: 11 };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF8CBAD" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  };

  const hr = sheet.getRow(headerRow);
  applyHeaderStyle(hr.getCell(1), "Part No.");
  applyHeaderStyle(hr.getCell(2), "Part Name");
  applyHeaderStyle(hr.getCell(3), "A/C");
  applyHeaderStyle(hr.getCell(4), "R/T");
  applyHeaderStyle(hr.getCell(7), "Admin (THB/pcs)");
  applyHeaderStyle(hr.getCell(8), "Profit (THB/pcs)");
  sheet.getColumn(1).width = 15;
  sheet.getColumn(2).width = 24;
  sheet.getColumn(3).width = 8;
  sheet.getColumn(4).width = 8;
  sheet.getColumn(7).width = 16;
  sheet.getColumn(8).width = 16;

  // ── Insert dynamic process columns (base shifted by COLUMN_OFFSET) ────
  let hCol = 32 + COLUMN_OFFSET;
  const insertDynamicColumn = (label: string, bgColor: string) => {
    hCol++;
    sheet.spliceColumns(hCol, 0, []);
    sheet.getColumn(hCol).width = Math.max(label.length + 2, 12);
    const targetCell = sheet.getRow(headerRow).getCell(hCol);
    targetCell.value = label;
    targetCell.font = { name: "Calibri", bold: true };
    targetCell.alignment = { vertical: "middle", horizontal: "center" };
    targetCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: `FF${bgColor.replace("#", "")}` },
    };
    targetCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  };
  const insertProcessColumns = (
    processes: Array<{ id: number; name: string }>,
    labelPrefix: string,
    color: string,
  ) => {
    if (!processes?.length) return;
    const sorted = [...processes].sort((a, b) => a.id - b.id);
    for (const process of sorted) {
      insertDynamicColumn(`${labelPrefix}${process.name}`, color);
    }
  };

  insertProcessColumns(
    breakdownProcess?.second_tier_process,
    "2nd Tier Process- ",
    "#c6e0b4",
  );
  hCol += 2; // skip second_tier_process_comment, other_process_total
  insertProcessColumns(
    breakdownProcess?.other_process,
    "Other Process Total- ",
    "#c6e0b4",
  );
  hCol += 4; // skip other_process_comment, percent, other_percent, oem_process_total
  insertProcessColumns(
    breakdownProcess?.oem_process,
    "OEM Process- ",
    "#ffe699",
  );
  hCol += 4; // skip oem_process_admin, oem_process_profit, spare_process_total
  insertProcessColumns(
    breakdownProcess?.spare_process,
    "S/P Process- ",
    "#ffe699",
  );
  sheet.getRow(headerRow).commit();

  // ── Build data rows for every part ───────────────────────────────────
  for (let pIdx = 0; pIdx < parts.length; pIdx++) {
    const p = parts[pIdx];
    const [childParts, detailsList] = await Promise.all([
      fetchChildPartV1ByPartId(p.part_id),
      fetchMaterialDetailByPartId(p.part_id, "v1"),
    ]);
    const materialCodeDetailsMap = Object.fromEntries(
      detailsList.map((d) => [d.material_id, d]),
    );

    for (const cp of childParts) {
      const materialCodeInfo = materialCodes?.find(
        (mc) => mc.material_code === cp.child_part_info.material_code,
      );
      const materialCodeDetail = materialCodeInfo?.material_id
        ? materialCodeDetailsMap[materialCodeInfo.material_id]
        : undefined;

      const { d: base, colNumber: baseCol } = buildChildPartV1RowData(
        cp,
        materialCodeDetail,
        breakdownProcess,
      );

      const row: RowData = [];
      row[1] = p.part_number;
      row[2] = p.part_name;
      row[3] = p.ac;
      row[4] = p.rt;
      row[5] = base[1]; // Child Part No.
      row[6] = base[2]; // Child Part Name
      row[7] = "0.00"; // Admin (THB/pcs)
      row[8] = "0.00"; // Profit (THB/pcs)
      for (let k = 3; k <= baseCol; k++) {
        row[k + COLUMN_OFFSET] = base[k];
      }
      colNumber = baseCol + COLUMN_OFFSET;
      rowsToInsert.push(row);
    }

    if (pIdx < parts.length - 1 && childParts.length > 0) {
      rowsToInsert.push([]);
      separatorOffsets.push(rowsToInsert.length - 1);
    }
    onProgress?.(pIdx + 1, parts.length);
  }

  return { rowsToInsert, colNumber, separatorOffsets };
};

export const buildChildPartRows = async (
  childPartsV2: childPartV2[],
  insertRowIndex: number,
  supplierCode: string,
  partId: number,
): Promise<{ rowsToInsert: RowData[]; mergeRequests: MergeRequest[] }> => {
  const rowsToInsert: RowData[] = [];
  const mergeRequests: MergeRequest[] = [];
  let currentRow = insertRowIndex;

  const [
    materialCodes,
    detailsList,
    material2ndTierProcesses,
    materialOtherProcesses,
  ] = await Promise.all([
    fetchMaterialCodesBySupplier(supplierCode || ""),
    fetchMaterialDetailByPartId(partId || 0, "v2"),
    fetchMaterial2ndTierProcesses(),
    fetchMaterialOtherProcesses(),
  ]);

  const secondTierProcessMap = Object.fromEntries(
    (material2ndTierProcesses || []).map((p: { id: number; name: string }) => [
      p.id,
      p.name,
    ]),
  );
  const otherProcessMap = Object.fromEntries(
    (materialOtherProcesses || []).map((p: { id: number; name: string }) => [
      p.id,
      p.name,
    ]),
  );

  const materialCodeDetailsMap = Object.fromEntries(
    detailsList.map((d) => [d.material_id, d]),
  );
  for (const part of childPartsV2) {
    const secondTierProcesses =
      part.child_part_material_cost.second_tier_process || [];
    const otherProcesses = part.child_part_material_cost.other_process || [];
    const childPartProcesses = part.child_part_process.cp_process || [];

    const materialCodeInfo = materialCodes?.find((mc) => {
      return mc.material_code === part.child_part_info.material_code;
    });
    const materialCodeDetail = materialCodeInfo?.material_id
      ? materialCodeDetailsMap[materialCodeInfo.material_id]
      : undefined;

    const rowCount = Math.max(
      secondTierProcesses.length,
      otherProcesses.length,
      childPartProcesses.length,
      1,
    );

    for (let r = 0; r < rowCount; r++) {
      const d: RowData = [];

      if (r === 0) {
        d[1] = part.child_part_no;
        d[2] = part.child_part_name;
        d[3] = part.child_part_info.qty;
        d[4] = materialCodeDetail?.supplier_name || "";
        d[5] = materialCodeDetail?.material_type_name || "";
        d[6] = materialCodeDetail?.spec_name || "";
        d[7] = materialCodeDetail?.dimension_name || "";
        d[8] = materialCodeDetail?.second_tier_supplier_name || "";
        d[9] = materialCodeDetail?.return_scrap_supplier_name || "";
        d[10] = materialCodeDetail?.first_tier_supplier_name || "";
        d[11] = materialCodeDetail?.thickness?.toString() || "";
        d[12] = materialCodeDetail?.width?.toString() || "";
        d[13] = materialCodeDetail?.diameter?.toString() || "";
        d[14] = materialCodeDetail?.shaft_od?.toString() || "";
        d[15] = materialCodeDetail?.round_od?.toString() || "";
        d[16] = materialCodeDetail?.rectangle_x?.toString() || "";
        d[17] = materialCodeDetail?.rectangle_y?.toString() || "";
        d[18] = materialCodeDetail?.length_mm?.toString() || "";
        d[19] = part.child_part_info.amount;
        d[20] = part.child_part_info.ratio;
        d[21] = part.child_part_info.consumption;
        d[22] = part.child_part_info.part_weight;
        d[23] = part.child_part_info.scrap_weight;
        d[24] = part.child_part_material_cost.material_cost;
        d[25] = part.child_part_material_cost.is_purchased ? "Yes" : "No";
        d[26] = part.child_part_material_cost.scrap_price;
        d[27] = part.child_part_material_cost.admin;
        d[28] = part.child_part_material_cost.profit;
        d[29] = part.child_part_material_cost.percent;
        d[30] = part.child_part_material_cost.other_percent;
        d[31] = part.total_material_cost;
        d[34] = part.child_part_material_cost.second_tier_process_total || "";
        d[35] = part.child_part_material_cost.second_tier_process_comment || "";
        d[38] = part.child_part_material_cost.other_process_total || "";
        d[39] = part.child_part_material_cost.other_process_comment || "";
        d[46] = part.child_part_process.cp_process_cost || "";
        d[47] = part.child_part_process.cp_process_total || "";
      }

      if (r < secondTierProcesses.length) {
        const process = secondTierProcesses[r];
        d[32] =
          secondTierProcessMap[
            process.part_material_second_tier_process_v2_id
          ] || "";
        d[33] = process.cost;
      }

      if (r < otherProcesses.length) {
        const process = otherProcesses[r];
        d[36] =
          otherProcessMap[process.part_material_other_process_v2_id] || "";
        d[37] = process.cost;
      }

      if (r < childPartProcesses.length) {
        const process = childPartProcesses[r];
        d[40] = process.part_process_v2_name;
        d[41] = process.detail;
        d[42] = process.qty_1;
        d[43] = process.cost_per_qty_1;
        d[44] = process.qty_2;
        d[45] = process.cost_per_qty_2;
        d[48] = process.tooling_name;
        d[49] = process.tooling_cost;
      }

      rowsToInsert.push(d);
    }

    if (rowCount > 1) {
      for (const col of MERGE_CHILDPARTS_COLS) {
        mergeRequests.push({
          startRow: currentRow,
          endRow: currentRow + rowCount - 1,
          col,
        });
      }
    }

    currentRow += rowCount;
  }

  return { rowsToInsert, mergeRequests };
};

export const buildPartProcessRows = (
  processes: Array<{
    process_name: string;
    detail: string;
    unit: number;
    price_per_unit: number;
    total_price: number;
  }>,
  admin: number,
  profit: number,
  sectionTotal: number,
  insertRowIndex: number,
): { rowsToInsert: RowData[]; mergeRequests: MergeRequest[] } => {
  const rowsToInsert: RowData[] = [];
  const mergeRequests: MergeRequest[] = [];
  const rowCount = processes.length || 1;

  for (let r = 0; r < rowCount; r++) {
    const d: RowData = [];

    if (r === 0) {
      d[7] = admin;
      d[8] = profit;
      d[9] = sectionTotal;
    }

    if (r < processes.length) {
      const process = processes[r];
      d[1] = r + 1;
      d[2] = process.process_name;
      d[3] = process.detail || "";
      d[4] = process.unit;
      d[5] = process.price_per_unit;
      d[6] = process.total_price;
    }

    rowsToInsert.push(d);
  }

  if (rowCount > 1) {
    for (const col of MERGE_PARTPROCESS_COLS) {
      mergeRequests.push({
        startRow: insertRowIndex,
        endRow: insertRowIndex + rowCount - 1,
        col,
      });
    }
  }

  return { rowsToInsert, mergeRequests };
};

export const buildPartOtherProcessRows = (
  processes: Array<{ process_name: string; detail?: string; price: number }>,
  admin: number,
  profit: number,
  sectionTotal: number,
  insertRowIndex: number,
  price: number,
): { rowsToInsert: RowData[]; mergeRequests: MergeRequest[] } => {
  const rowsToInsert: RowData[] = [];
  const mergeRequests: MergeRequest[] = [];
  const rowCount = processes.length || 1;

  for (let r = 0; r < rowCount; r++) {
    const d: RowData = [];

    if (r === 0) {
      d[4] = admin;
      d[5] = profit;
      d[6] = sectionTotal;
      d[7] = price;
    }

    if (r < processes.length) {
      const process = processes[r];
      d[1] = r + 1;
      d[2] = process.process_name;
      d[3] = process.price;
    }

    rowsToInsert.push(d);
  }

  if (rowCount > 1) {
    for (const col of MERGE_PARTOTHERPROCESS_COLS) {
      mergeRequests.push({
        startRow: insertRowIndex,
        endRow: insertRowIndex + rowCount - 1,
        col,
      });
    }
  }

  return { rowsToInsert, mergeRequests };
};
